/**
 * LA TÂCHE D'ÉCRITURE — la production écrite longue du français (chantier
 * « Rétroaction », P3-a, AB#878 ; gabarit `FR-ECR` de
 * `docs/pilotage/plan-de-contenu/donnees/cadre.json`, capacité `CAP-04`).
 *
 * STRUCTURES SEULEMENT : aucun contenu pédagogique, aucun appel au modèle.
 *
 * ─── Ce qu'une tâche d'écriture porte ──────────────────────────────────────
 *
 *  - ce que l'ÉLÈVE lit avant d'écrire (`ConsigneEcriture`, écrite dans
 *    `ContentItem.enonce.ecriture`) : le genre, la longueur cible en mots, les
 *    contraintes que le CODE sait vérifier ;
 *  - la GRILLE PROPRE À LA TÂCHE (le format `GrilleCorrection` existant, écrite
 *    dans `ContentItem.grilleCorrection`) : ses critères sont ceux du catalogue
 *    `ECR_*` (`packages/api/src/modules/corpus/donnees/francais.criteres.ts`),
 *    avec des indicateurs propres à la tâche ;
 *  - la GRILLE MINISTÉRIELLE à cinq critères et sa pondération PAR NIVEAU,
 *    rangée dans les trois niveaux montrés à l'élève — idées, cohérence,
 *    langue (`Retroaction.competences`). Elle n'est pas stockée : elle se
 *    déduit du niveau du chapitre, ici.
 *
 * ─── « Compter n'est pas juger » ───────────────────────────────────────────
 *
 * Une contrainte n'entre ici que si du code peut dire, sans modèle et sans
 * interprétation, si elle est tenue : un nombre de paragraphes, des marqueurs
 * présents, des citations entre guillemets, des mots exigés. « Le texte est
 * bien organisé » n'est PAS une contrainte : c'est un critère de la grille, lu
 * par le correcteur. Les mesures sont dans `../retroaction/mesures-texte.ts`.
 *
 * ─── Aucun pourcentage face à l'élève ──────────────────────────────────────
 *
 * Les pondérations servent au rangement et à la cote par critère ; l'écran de
 * l'élève montre des états et des constats par niveau (§ 6 des instructions de
 * production), jamais « 72 % ».
 */
import { z } from 'zod';
/* ══════════════════════════════════════════════════════════════════════════
   Le genre et la longueur
   ══════════════════════════════════════════════════════════════════════════ */
/**
 * Les genres d'une tâche d'écriture. L'argumentatif (opinion argumentée,
 * lettre ouverte) est surtout de 4e et de 5e secondaire ; le genre de 3e retenu
 * pour le pilote est l'explicatif (PDA, pp. 14-16 — décision du fondateur en
 * attente, PLAN.md § 5, question 5).
 */
export const GENRES_TEXTE = [
    'explicatif',
    'justificatif',
    'narratif',
    'descriptif',
    'argumentatif',
];
/**
 * Le plafond de longueur d'une tâche, en mots. La réponse rédigée est plafonnée
 * à `MAX_CARACTERES_TEXTE` = 4 000 caractères (`manuel-exercices.ts`) : à un
 * peu plus de six caractères par mot en français, espace compris, 550 mots y
 * tiennent ; une lettre ouverte de 5e « d'environ 500 mots » y tient de
 * justesse (`CAP-04`). Relever ce plafond appartient à la remise d'un texte (P3-c).
 */
export const MAX_MOTS_TACHE_ECRITURE = 550;
/* ══════════════════════════════════════════════════════════════════════════
   Les contraintes vérifiables par le code
   ══════════════════════════════════════════════════════════════════════════ */
/** Les sortes de contraintes que `mesures-texte.ts` sait vérifier. */
export const SORTES_CONTRAINTE_ECRITURE = [
    'paragraphes',
    'phrases',
    'marqueurs',
    'citations',
    'mots_exiges',
];
/** Le code réservé à la longueur, ajouté par la mesure à la liste des contraintes. */
export const CODE_CONTRAINTE_LONGUEUR = 'LONGUEUR';
const texteNonVide = z.string().trim().min(1);
/** Code stable d'une contrainte : `PARAGRAPHES`, `MARQUEURS_EXPLICATION`. C'est `mesures.contraintes[].code`. */
const codeContrainte = z
    .string()
    .regex(/^[A-Z][A-Z0-9_]*$/, 'code de contrainte attendu : PARAGRAPHES, MARQUEURS_CAUSE')
    .refine((c) => c !== CODE_CONTRAINTE_LONGUEUR, 'LONGUEUR est réservé à la longueur cible');
const entier = z.number().int().min(0);
/** Commun à toutes les sortes : un code stable, et ce que l'élève lit (au tutoiement). */
const communContrainte = {
    code: codeContrainte,
    /** Ce que l'élève lit sous la consigne : « Ton texte compte trois paragraphes. » */
    libelle: texteNonVide.max(200),
};
/** Une borne : au moins `min`, au plus `max` ; l'une des deux au moins. */
const borne = { min: entier.optional(), max: entier.optional() };
export const contrainteEcritureSchema = z
    .discriminatedUnion('sorte', [
    /** Le nombre de paragraphes (blocs séparés par un saut de ligne). */
    z.object({ sorte: z.literal('paragraphes'), ...communContrainte, ...borne }).strict(),
    /** Le nombre de phrases. */
    z.object({ sorte: z.literal('phrases'), ...communContrainte, ...borne }).strict(),
    /**
     * Des marqueurs de relation : au moins `min` marqueurs DIFFÉRENTS de la
     * liste sont présents (« d'abord », « en effet », « par conséquent »). Le
     * code voit la présence, pas la justesse du lien : celle-ci est le critère
     * `ECR_MARQUEURS`, lu par le correcteur.
     */
    z
        .object({
        sorte: z.literal('marqueurs'),
        ...communContrainte,
        liste: z.array(texteNonVide.max(40)).min(1).max(60),
        min: z.number().int().min(1),
    })
        .strict(),
    /**
     * Le discours rapporté DIRECT : passages entre guillemets français ou
     * anglais, et répliques ouvertes par un tiret. Le discours indirect ne se
     * compte pas : il se juge.
     */
    z.object({ sorte: z.literal('citations'), ...communContrainte, ...borne }).strict(),
    /** Des mots ou expressions qui doivent figurer tels quels (forme exacte, casse ignorée). */
    z
        .object({
        sorte: z.literal('mots_exiges'),
        ...communContrainte,
        mots: z.array(texteNonVide.max(60)).min(1).max(20),
    })
        .strict(),
])
    .superRefine((c, ctx) => {
    if (c.sorte === 'marqueurs' || c.sorte === 'mots_exiges')
        return;
    if (c.min === undefined && c.max === undefined) {
        ctx.addIssue({ code: 'custom', message: 'une borne au moins : min ou max' });
    }
    else if (c.min !== undefined && c.max !== undefined && c.min > c.max) {
        ctx.addIssue({ code: 'custom', message: 'min dépasse max' });
    }
});
/* ══════════════════════════════════════════════════════════════════════════
   La consigne d'écriture — ce que l'élève lit, `ContentItem.enonce.ecriture`
   ══════════════════════════════════════════════════════════════════════════ */
export const consigneEcritureSchema = z
    .object({
    genre: z.enum(GENRES_TEXTE),
    /** Facultatif : à qui l'élève écrit (« les lecteurs du journal de l'école »). */
    destinataire: texteNonVide.max(200).optional(),
    /** La longueur CIBLE, en mots comptés par `compterMotsFrancais` (`mesures-texte.ts`). */
    longueur: z
        .object({
        min: z.number().int().min(20),
        max: z.number().int().max(MAX_MOTS_TACHE_ECRITURE),
    })
        .strict()
        .refine((l) => l.min < l.max, 'la longueur minimale est inférieure à la maximale'),
    contraintes: z.array(contrainteEcritureSchema).max(12),
})
    .strict()
    .superRefine((c, ctx) => {
    const codes = c.contraintes.map((k) => k.code);
    const doublons = codes.filter((k, i) => codes.indexOf(k) !== i);
    for (const d of new Set(doublons)) {
        ctx.addIssue({ code: 'custom', path: ['contraintes'], message: `contrainte répétée : ${d}` });
    }
});
/* ══════════════════════════════════════════════════════════════════════════
   La grille ministérielle et les trois niveaux montrés à l'élève
   ══════════════════════════════════════════════════════════════════════════ */
/** Les trois niveaux montrés à l'élève — les codes français de `Retroaction.competences`. */
export const NIVEAUX_ECRITURE = ['idees', 'coherence', 'langue'];
/**
 * Les cinq critères notés du cadre d'évaluation du Ministère, dans l'ordre du
 * Ministère, par leur code du catalogue `ECR_*`.
 */
export const CRITERES_MINISTERIELS_ECRITURE = [
    'ECR_ADAPTATION',
    'ECR_COHERENCE',
    'ECR_VOCABULAIRE',
    'ECR_PHRASES',
    'ECR_ORTHOGRAPHE',
];
/**
 * Les trois grains fins de la cohérence (catalogue `ECR_*`) : ils servent au
 * diagnostic et se replient sur `ECR_COHERENCE` au bulletin.
 */
export const CRITERES_FINS_ECRITURE = ['ECR_MARQUEURS', 'ECR_REPRISE', 'ECR_PROGRESSION'];
/** Le libellé de chaque critère, dans nos mots (repris de `francais.criteres.ts`). */
export const LIBELLES_CRITERE_ECRITURE = {
    ECR_ADAPTATION: 'Adaptation à la situation de communication',
    ECR_COHERENCE: 'Cohérence du texte',
    ECR_VOCABULAIRE: 'Utilisation d’un vocabulaire approprié',
    ECR_PHRASES: 'Construction des phrases et ponctuation',
    ECR_ORTHOGRAPHE: 'Respect des normes orthographiques',
    ECR_MARQUEURS: 'Emploi des marqueurs de relation',
    ECR_REPRISE: 'Reprise de l’information',
    ECR_PROGRESSION: 'Progression de l’information',
};
/**
 * LE RANGEMENT en trois niveaux : l'adaptation dit les IDÉES et la structure ;
 * la cohérence (et ses trois grains fins) dit la COHÉRENCE ; vocabulaire,
 * construction des phrases et ponctuation, orthographe disent la LANGUE.
 */
export const NIVEAU_DU_CRITERE_ECRITURE = {
    ECR_ADAPTATION: 'idees',
    ECR_COHERENCE: 'coherence',
    ECR_MARQUEURS: 'coherence',
    ECR_REPRISE: 'coherence',
    ECR_PROGRESSION: 'coherence',
    ECR_VOCABULAIRE: 'langue',
    ECR_PHRASES: 'langue',
    ECR_ORTHOGRAPHE: 'langue',
};
export function estCritereEcriture(code) {
    return Object.prototype.hasOwnProperty.call(NIVEAU_DU_CRITERE_ECRITURE, code);
}
/**
 * LES PONDÉRATIONS PAR NIVEAU (`INSTRUCTIONS-DE-PRODUCTION.md` § 6).
 *
 *  - 2e secondaire : 25 / 20 / 10 / 25 / 20 (épreuve d'environ 300 mots) ;
 *  - 5e secondaire : 30 / 20 / 5 / 25 / 20 (lettre ouverte d'environ 500 mots) ;
 *  - 3e secondaire : 25 / 20 / 10 / 25 / 20 — la grille de 2e, reprise telle
 *    quelle (décision du fondateur du 16 oct. 2026 ; PLAN.md § 5, question 5) ;
 *  - 1re secondaire : la grille de 2e ; 4e secondaire : la grille de 5e — le
 *    Ministère n'a pas d'épreuve d'écriture à ces niveaux (décision du
 *    fondateur du 16 oct. 2026).
 *
 * Un niveau absent de cette table : une tâche d'écriture y est refusée à
 * l'import tant que sa pondération n'est pas décidée.
 */
export const PONDERATIONS_MINISTERIELLES = {
    sec1: {
        poids: {
            ECR_ADAPTATION: 25,
            ECR_COHERENCE: 20,
            ECR_VOCABULAIRE: 10,
            ECR_PHRASES: 25,
            ECR_ORTHOGRAPHE: 20,
        },
        provisoire: false,
        source: 'décision du fondateur du 16 oct. 2026 : reprise de la grille ministérielle de 2e secondaire',
    },
    sec2: {
        poids: {
            ECR_ADAPTATION: 25,
            ECR_COHERENCE: 20,
            ECR_VOCABULAIRE: 10,
            ECR_PHRASES: 25,
            ECR_ORTHOGRAPHE: 20,
        },
        provisoire: false,
        source: 'épreuve obligatoire de 2e secondaire (INSTRUCTIONS-DE-PRODUCTION.md § 6)',
    },
    sec3: {
        poids: {
            ECR_ADAPTATION: 25,
            ECR_COHERENCE: 20,
            ECR_VOCABULAIRE: 10,
            ECR_PHRASES: 25,
            ECR_ORTHOGRAPHE: 20,
        },
        provisoire: false,
        source: 'décision du fondateur du 16 oct. 2026 : reprise de la grille ministérielle de 2e secondaire',
    },
    sec4: {
        poids: {
            ECR_ADAPTATION: 30,
            ECR_COHERENCE: 20,
            ECR_VOCABULAIRE: 5,
            ECR_PHRASES: 25,
            ECR_ORTHOGRAPHE: 20,
        },
        provisoire: false,
        source: 'décision du fondateur du 16 oct. 2026 : reprise de la grille ministérielle de 5e secondaire',
    },
    sec5: {
        poids: {
            ECR_ADAPTATION: 30,
            ECR_COHERENCE: 20,
            ECR_VOCABULAIRE: 5,
            ECR_PHRASES: 25,
            ECR_ORTHOGRAPHE: 20,
        },
        provisoire: false,
        source: 'épreuve unique de 5e secondaire (INSTRUCTIONS-DE-PRODUCTION.md § 6)',
    },
};
export function ponderationMinisterielle(niveau) {
    return (PONDERATIONS_MINISTERIELLES[niveau] ??
        null);
}
/**
 * Range les critères d'une grille dans les trois niveaux, dans l'ordre
 * idées → cohérence → langue. Un critère hors du catalogue `ECR_*` n'est rangé
 * nulle part : il est rendu à part (`horsNiveaux`) — la validation de l'import
 * le refuse pour une tâche d'écriture.
 */
export function rangerEnTroisNiveaux(grille) {
    const niveaux = NIVEAUX_ECRITURE.map((niveau) => {
        const criteres = grille.criteres.filter((k) => estCritereEcriture(k.code) && NIVEAU_DU_CRITERE_ECRITURE[k.code] === niveau);
        return { niveau, poids: criteres.reduce((s, k) => s + k.poids, 0), criteres };
    });
    return { niveaux, horsNiveaux: grille.criteres.filter((k) => !estCritereEcriture(k.code)) };
}
/**
 * Le seuil de réussite de la grille ministérielle : 60 sur 100, la note de
 * passage de l'école québécoise. Il ne s'affiche jamais en pourcentage.
 */
export const SEUIL_REUSSITE_MINISTERIEL = 0.6;
/**
 * La grille ministérielle d'un niveau : les cinq critères, leur libellé, les
 * indicateurs (ceux de la tâche quand elle en donne, sinon le libellé seul) et
 * leur poids. `null` si le niveau n'a pas de pondération connue.
 */
export function grilleMinisterielle(niveauScolaire, indicateursDeLaTache = {}) {
    const p = ponderationMinisterielle(niveauScolaire);
    if (p === null)
        return null;
    const grille = {
        version: 1,
        seuilReussite: SEUIL_REUSSITE_MINISTERIEL,
        criteres: CRITERES_MINISTERIELS_ECRITURE.map((code) => ({
            code,
            libelle: LIBELLES_CRITERE_ECRITURE[code],
            indicateurs: [...(indicateursDeLaTache[code] ?? [LIBELLES_CRITERE_ECRITURE[code]])],
            poids: p.poids[code],
        })),
    };
    return {
        niveauScolaire,
        provisoire: p.provisoire,
        source: p.source,
        grille,
        niveaux: rangerEnTroisNiveaux(grille).niveaux,
    };
}
/**
 * Ce qui empêche une consigne et sa grille de faire une tâche d'écriture, à ce
 * niveau. Vide : la tâche tient. Pure — l'import l'appelle (`valider-exercices.ts`).
 *
 * `complete` : une production COMPLÈTE (situation-problème de niveau 4) est
 * jugée sur les cinq critères ministériels, chacun au poids de son niveau. Une
 * production DÉCOUPÉE (une introduction, un paragraphe) prend une partie des
 * critères `ECR_*`, à ses poids.
 */
export function griefsTacheEcriture(consigne, grille, niveauScolaire, complete) {
    const griefs = [];
    const p = ponderationMinisterielle(niveauScolaire);
    if (p === null) {
        griefs.push(`aucune pondération ministérielle connue en ${niveauScolaire} : une tâche d’écriture n’y entre pas avant la décision`);
    }
    for (const k of grille.criteres) {
        if (!estCritereEcriture(k.code)) {
            griefs.push(`critère hors du catalogue d’écriture : ${k.code} (attendu : ${[...CRITERES_MINISTERIELS_ECRITURE, ...CRITERES_FINS_ECRITURE].join(', ')})`);
        }
    }
    if (complete) {
        const codes = grille.criteres.map((k) => k.code).sort();
        if (codes.join() !== [...CRITERES_MINISTERIELS_ECRITURE].sort().join()) {
            griefs.push(`une production complète se juge sur les cinq critères du Ministère, et eux seuls : ${CRITERES_MINISTERIELS_ECRITURE.join(', ')}`);
        }
        else if (p !== null) {
            for (const k of grille.criteres) {
                const attendu = p.poids[k.code];
                if (k.poids !== attendu) {
                    griefs.push(`${k.code} : poids ${k.poids}, la pondération de ${niveauScolaire} dit ${attendu}${p.provisoire ? ' (PROVISOIRE)' : ''}`);
                }
            }
        }
    }
    for (const c of consigne.contraintes) {
        if (c.sorte === 'marqueurs' && c.min > new Set(c.liste.map((m) => m.toLowerCase())).size) {
            griefs.push(`${c.code} : on exige ${c.min} marqueurs différents d’une liste qui en compte moins`);
        }
        if (c.sorte === 'paragraphes' && c.min !== undefined && c.min * 20 > consigne.longueur.max) {
            griefs.push(`${c.code} : ${c.min} paragraphes ne tiennent pas en ${consigne.longueur.max} mots`);
        }
    }
    return griefs;
}
/** Assemble une tâche, ou dit pourquoi elle ne tient pas. */
export function tacheEcriture(consigne, grille, niveauScolaire, complete) {
    const griefs = griefsTacheEcriture(consigne, grille, niveauScolaire, complete);
    const ministerielle = grilleMinisterielle(niveauScolaire);
    if (griefs.length > 0 || ministerielle === null)
        return { ok: false, griefs };
    return { ok: true, tache: { consigne, grille, ministerielle } };
}
//# sourceMappingURL=tache-ecriture.js.map